import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, ExternalLink, FileSpreadsheet, Cloud } from 'lucide-react';
import { IntegrationCard } from './IntegrationCard';

interface OneDriveFile {
  id: string;
  name: string;
  size?: number;
  lastModifiedDateTime?: string;
  webUrl?: string;
}

export const OneDriveExcelIntegration = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [files, setFiles] = useState<OneDriveFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    setChecking(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('microsoft_tokens')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      setIsConnected(!!data);
    } catch (error) {
      console.error('Error checking OneDrive connection:', error);
    } finally {
      setChecking(false);
    }
  };

  const handleOAuthConnect = async () => {
    setIsConnecting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Call the enhanced OAuth integration for Microsoft
      const { data, error } = await supabase.functions.invoke('enhanced-oauth-integration', {
        body: { action: 'initialize_oauth', provider: 'microsoft' },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to initialize OAuth');
      }

      if (data?.authUrl) {
        // Redirect to OAuth authorization URL
        window.location.href = data.authUrl;
      } else {
        throw new Error('No authorization URL received');
      }
    } catch (error: any) {
      console.error('OneDrive OAuth error:', error);
      toast.error(error.message || 'Failed to start OAuth flow');
      setIsConnecting(false);
    }
  };

  const loadFiles = async () => {
    if (!isConnected) return;

    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase.functions.invoke('enhanced-oauth-integration', {
        body: { 
          action: 'list_files',
          provider: 'microsoft',
          fileType: 'excel'
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      setFiles(data?.files || []);
      toast.success(`Loaded ${data?.files?.length || 0} Excel files from OneDrive`);
    } catch (error: any) {
      console.error('Error loading files:', error);
      toast.error('Failed to load files from OneDrive');
    } finally {
      setIsLoading(false);
    }
  };


  const runAIWorkflow = async () => {
    try {
      toast.info('Starting AI workflow with OneDrive + Excel data...');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { error } = await supabase.functions.invoke('enhanced-data-refresh', {
        body: { 
          source: 'onedrive_excel',
          includeAIWorkflow: true
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      toast.success('AI workflow with OneDrive + Excel started successfully!');
    } catch (error: any) {
      console.error('Error running AI workflow:', error);
      toast.error('Failed to run AI workflow');
    }
  };

  const disconnect = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('microsoft_tokens')
        .delete()
        .eq('user_id', user.id);

      setIsConnected(false);
      setFiles([]);
      toast.success('Disconnected from OneDrive');
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Failed to disconnect from OneDrive');
    }
  };

  return (
    <IntegrationCard
      icon={
        <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
          <Cloud className="w-4 h-4 text-white" />
        </div>
      }
      name="OneDrive"
      description="Sign in with your own Microsoft account so Veylo can read the Excel files in your OneDrive."
      isConnected={isConnected}
      checking={checking}
      isConnecting={isConnecting}
      onConnect={handleOAuthConnect}
      onDisconnect={disconnect}
      actions={
        <>
          <Button onClick={loadFiles} disabled={isLoading} variant="outline">
            {isLoading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading…</>
            ) : (
              'Load Files'
            )}
          </Button>
          <Button onClick={runAIWorkflow} variant="outline">Run AI Workflow</Button>
        </>
      }
    >
      {files.length > 0 ? (
        <div>
          <h4 className="font-medium mb-2">Excel files ({files.length})</h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {files.map((file) => (
              <div key={file.id} className="p-3 border rounded-lg">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4 text-green-700" />
                  <div className="font-medium">{file.name}</div>
                </div>
                <div className="text-sm text-muted-foreground">
                  {file.size !== undefined && `${(file.size / 1024).toFixed(1)} KB`}
                  {file.lastModifiedDateTime &&
                    ` · Modified ${new Date(file.lastModifiedDateTime).toLocaleDateString()}`}
                </div>
                {file.webUrl && (
                  <a
                    href={file.webUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline flex items-center gap-1"
                  >
                    Open in Excel Online
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        !isLoading && (
          <p className="text-sm text-muted-foreground">
            No files loaded yet. Choose "Load Files" to read your OneDrive.
          </p>
        )
      )}
    </IntegrationCard>
  );
};
